import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plug, Activity, Target, Swords, ListChecks } from "lucide-react";
import { api } from "../lib/api";

function Counter({ Icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="shrink-0 grid place-items-center w-7 h-7 rounded-md bg-[#0c0c0c] border border-[#E5B842]/25">
        <Icon className="w-3.5 h-3.5 text-[#E5B842]" />
      </span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono leading-tight">{label}</div>
        <div className="font-heading text-base font-black text-slate-100 tabular-nums leading-none mt-0.5">{value}</div>
      </div>
    </div>
  );
}

// Read-only Passport block — real counts per linked partner × product. Never
// implies rating impact; external events are stored but not yet rated.
export function ConnectedActivity({ wsbfId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/players/${wsbfId}/connected-activity`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ partners: [] }); });
    return () => { alive = false; };
  }, [wsbfId]);

  if (!data) {
    return (
      <div className="rounded-xl bg-[#141414] border border-[rgba(212,175,55,0.18)] p-5 animate-pulse h-24" data-testid="connected-activity-loading" />
    );
  }
  if ((data.partners || []).length === 0) {
    return null; // hide block entirely if no linked partners
  }

  return (
    <div className="rounded-xl bg-[#141414] border border-[rgba(212,175,55,0.18)] p-5" data-testid="passport-connected-activity">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="font-heading text-sm font-black uppercase tracking-tight text-slate-100 flex items-center gap-2">
          <Plug className="w-4 h-4 text-[#E5B842]" /> Connected Activity
        </div>
        <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">external · attribution only</span>
      </div>
      <div className="space-y-3">
        {data.partners.map((p) => (
          <div key={p.partner_id} data-testid={`connected-activity-${p.partner_slug}`}
            className="rounded-lg bg-[#0a0a0a] border border-[rgba(212,175,55,0.14)] p-4">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                <span className="font-heading font-bold text-slate-100">{p.partner_name}</span>
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">· connected</span>
              </div>
              <div className="text-[10px] font-mono text-slate-500">
                last sync {p.last_sync ? new Date(p.last_sync).toLocaleDateString() : "—"}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Counter Icon={Activity} label="Sportsbook" value={`${p.activity.SPORTSBOOK.settled}`} />
              <Counter Icon={Target} label="ArenaBook" value={`${p.activity.ARENABOOK.count}`} />
              <Counter Icon={Swords} label="Challenge" value={`${p.activity.CHALLENGE.count}`} />
              <Counter Icon={ListChecks} label="Strike 5" value={`${p.activity.STRIKE5.count}`} />
            </div>
            {p.activity.SPORTSBOOK.settled > 0 && (
              <div className="mt-3 text-[11px] font-mono text-slate-500" data-testid={`${p.partner_slug}-sportsbook-breakdown`}>
                Sportsbook split · PREGAME {p.activity.SPORTSBOOK.pregame} · LIVE {p.activity.SPORTSBOOK.live}
              </div>
            )}
            <div className="mt-3 text-[10px] font-mono text-slate-600">
              External results count toward attribution and filtering. Official WSTF ratings include integrated activity only — see
              <Link to="/rankings" className="ml-1 text-[#E5B842] hover:underline">Rankings</Link>.
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
